import { FC } from "react"

import { ReactComponent as IconX } from "@common_assets/svg/cross.svg"
import { AccordionDetails, Grid, Stack, Typography, Box } from "@mui/material"
import QuantityStepper from "components/common/button/quantityStepper"
import ErrorText from "components/common/error/errorText"
import NoElementsMessage from "components/common/listing/noElementsMessage"
import { useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { ITransactionItem } from "ts/interfaces/Transaction"
import { TransactionType } from "ts/types/Transaction"

import Tile from "../tile"
import TilesSkeleton from "../tilesSkeleton"

interface IProps {
  children?: React.ReactNode
  selectedItems: ITransactionItem[]
  noElementsMessage: string
  variant: TransactionType
  data: ITransactionItem[] | undefined
  isLoading: boolean
  isError: boolean
  studentId?: number
  onTileClick: (item: ITransactionItem) => void
  fieldName: string
}

const DetailsTransaction: FC<IProps> = ({
  children,
  selectedItems,
  noElementsMessage,
  variant,
  data,
  isLoading,
  studentId,
  isError,
  onTileClick,
  fieldName,
}) => {
  const { t } = useTranslation()

  const { setValue } = useFormContext()

  const removeThisSameValues = (item: ITransactionItem) => {
    if (selectedItems?.find((variant) => variant.id === item.id)) {
      setValue(fieldName, [...selectedItems.filter((v) => v.id !== item.id)])
    }
  }

  const removeOneValue = (item: ITransactionItem) => {
    const index = selectedItems.map((v) => v.id).lastIndexOf(item.id)
    if (index === -1) return
    setValue(
      fieldName,
      selectedItems.filter((_, i) => i !== index),
    )
  }

  return (
    <AccordionDetails>
      <Stack gap="16px">
        <Grid container rowSpacing="16px" columnSpacing="12px">
          {!isLoading &&
            data
              ?.sort((a, b) => a.title.localeCompare(b.title))
              .map((tile) => {
                const quantity = selectedItems.filter(
                  (item) => item.id === tile.id,
                ).length
                return (
                  <Grid
                    key={tile.id}
                    position="relative"
                    size={{ mobile: 6, desktop: 4 }}
                  >
                    {/* Anchors the stepper to the tile, which may be shorter than the cell. */}
                    <Box position="relative" data-tile-cell>
                      <Tile
                        onClick={() => onTileClick(tile)}
                        title={tile.title}
                        amount={tile.amount}
                        variant={variant}
                        quantity={quantity}
                        selected={quantity > 0}
                      />
                      {quantity > 0 && (
                        <Box position="absolute" right="12px" bottom="12px">
                          <QuantityStepper
                            quantityText={`x${quantity}`}
                            decreaseLabel={t("transaction.decreaseItem", {
                              title: tile.title,
                            })}
                            increaseLabel={t("transaction.increaseItem", {
                              title: tile.title,
                            })}
                            onDecrease={(event) => {
                              // The stepper unmounts when the last unit goes.
                              if (quantity === 1) {
                                event.currentTarget
                                  .closest<HTMLElement>("[data-tile-cell]")
                                  ?.querySelector<HTMLElement>("[aria-pressed]")
                                  ?.focus()
                              }
                              removeOneValue(tile)
                            }}
                            onIncrease={() => onTileClick(tile)}
                          />
                        </Box>
                      )}
                    </Box>
                    {quantity > 0 && (
                      <Box
                        role="button"
                        tabIndex={0}
                        aria-label={t("transaction.removeItem", {
                          title: tile.title,
                        })}
                        onClick={() => {
                          removeThisSameValues(tile)
                        }}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault()
                            removeThisSameValues(tile)
                          }
                        }}
                        sx={(theme) => ({
                          position: "absolute",
                          top: "15px",
                          right: 0,
                          cursor: "pointer",
                          ":hover": {
                            transition: "color 0.3s",
                            color: theme.palette.mockup.error50,
                          },
                        })}
                      >
                        <IconX />
                      </Box>
                    )}
                  </Grid>
                )
              })}
        </Grid>
        {!isLoading && data?.length === 0 && (
          <NoElementsMessage title={noElementsMessage} />
        )}
        {isLoading && <TilesSkeleton />}
        {isError && <ErrorText />}
        {!data && (
          <Typography
            variant="subtitle1"
            textAlign="center"
            py="44px"
            color="mockup.darkBlue"
          >
            {studentId
              ? t(
                  "transaction.youDonyHavePermissionToTheListYouCanOnlySendTransactionsByCustomInput",
                )
              : t("transaction.toSeeTheListSelectStudentAndTheirClass")}
          </Typography>
        )}
        {children}
      </Stack>
    </AccordionDetails>
  )
}

export default DetailsTransaction
